import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { PeriodPicker } from '@/components/shared/date/PeriodPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat, localDateToUtcDate, utcDateToLocalDate } from '@/helpers/dateHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import {
  LeaveAllowancePeriod,
  ReadEmployee,
  useGetEmployeeLeaveAllowancesQuery,
  useGetOrganisationSettingsQuery,
  useSetEmployeeLeaveAllowanceMutation
} from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { FC, FormEvent, Suspense, useEffect, useMemo, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TSTitleCardStyle } from '../AddSingleTeamMemberForm';
import {
  currentPeriodStart,
  editKey,
  isValidAmount,
  LeaveBalanceEdit,
  leaveYearStart,
  parseYearParam,
  periodStartDate,
  shiftPeriod,
  YEARLY
} from './leaveBalanceHelpers';

type Edits = Record<string, LeaveBalanceEdit>;

interface SectionProps {
  employeeId: string;
  pickerLabel: string;
  period: LeaveAllowancePeriod;
  leaveYearStartMonth: number;
  edits: Edits;
  onEdit: (key: string, edit: LeaveBalanceEdit) => void;
  onClearEdit: (key: string) => void;
  initialPeriodStart?: Date;
}

const LeaveBalanceSection: FC<SectionProps> = ({
  employeeId,
  pickerLabel,
  period,
  leaveYearStartMonth,
  edits,
  onEdit,
  onClearEdit,
  initialPeriodStart
}) => {
  const s = strings.timeshift.teamMember.annualLeave.editForm;

  const [value, setValue] = useState(() => initialPeriodStart ?? currentPeriodStart(period, leaveYearStartMonth));
  useEffect(() => {
    setValue(initialPeriodStart ?? currentPeriodStart(period, leaveYearStartMonth));
  }, [period, leaveYearStartMonth, initialPeriodStart]);

  const periodStart = dateToApiFormat(value);
  const pickerDate = useMemo(() => utcDateToLocalDate(value), [value]);

  const [{ data, fetching }] = useGetEmployeeLeaveAllowancesQuery({
    variables: { employeeId, period, periodStart }
  });

  const allowances = data?.employeeLeaveAllowances ?? [];

  return (
    <section className="flex flex-col gap-4">
      <PeriodPicker
        label={pickerLabel}
        date={pickerDate}
        displayFormat={period === YEARLY ? 'yyyy' : 'MMMM yyyy'}
        yearOnly={period === YEARLY}
        yearStartMonth={leaveYearStartMonth}
        onPick={d => setValue(periodStartDate(period, localDateToUtcDate(d), leaveYearStartMonth))}
        onPrevious={() => setValue(v => shiftPeriod(period, v, -1))}
        onNext={() => setValue(v => shiftPeriod(period, v, 1))}
      />

      {fetching && !data ? (
        <TableLoader />
      ) : allowances.length === 0 ? (
        <p className="text-sm text-gray-500">{s.noLimits}</p>
      ) : (
        allowances.map(allowance => {
          const leaveTypeId = allowance.leaveType.id;
          const key = editKey(period, periodStart, leaveTypeId);
          const edit = edits[key];

          const amount = edit?.amount ?? String(allowance.amount);
          const isRecurring = edit?.isRecurring ?? allowance.isRecurring;
          const invalid = !isValidAmount(amount);
          const inputId = `${period}-${leaveTypeId}`;

          const update = (patch: Partial<Pick<LeaveBalanceEdit, 'amount' | 'isRecurring'>>) => {
            const next = { amount, isRecurring, ...patch };
            if (next.amount === String(allowance.amount) && next.isRecurring === allowance.isRecurring) {
              onClearEdit(key);
            } else {
              onEdit(key, { period, periodStart, leaveTypeId, ...next });
            }
          };

          return (
            <div key={leaveTypeId} className="flex flex-col gap-1">
              <label htmlFor={inputId} className="text-sm font-semibold">
                {allowance.leaveType.name}
              </label>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <input
                  id={inputId}
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={amount}
                  onChange={e => update({ amount: e.target.value })}
                  aria-invalid={invalid}
                  className={`h-10 w-80 max-w-full rounded-md border px-3 focus:outline-none focus-visible:ring-2 ${
                    invalid
                      ? 'border-red-500 focus-visible:ring-red-500'
                      : 'border-gray-300 focus-visible:ring-green-600'
                  }`}
                />

                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={e => update({ isRecurring: e.target.checked })}
                    className="h-5 w-5 rounded border-gray-300 accent-green-600"
                  />
                  <span>{s.recurring}</span>
                </label>
              </div>
              {invalid && <span className="text-sm text-red-600">{s.invalidAmount}</span>}
              <div className="flex flex-col flex-wrap gap-x-4 text-sm text-gray-600">
                {allowance.carriedOver > 0 && <span>{s.daysCarriedOver(allowance.carriedOver)}</span>}
                {allowance.taken > 0 && <span>{s.daysTaken(allowance.taken)}</span>}
              </div>
            </div>
          );
        })
      )}
    </section>
  );
};

export const TeamMemberLeaveBalanceEditForm: FC<{ employee: ReadEmployee }> = ({ employee }) => {
  const s = strings.timeshift.teamMember.annualLeave.editForm;
  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const [edits, setEdits] = useState<Edits>({});
  const [saving, setSaving] = useState(false);
  const [, setAllowance] = useSetEmployeeLeaveAllowanceMutation();
  const [{ data: settingsData }] = useGetOrganisationSettingsQuery();
  const leaveYearStartMonth = settingsData?.organisationSettings?.leaveYearStartMonth ?? 1;

  const [searchParams] = useSearchParams();

  const urlYear = parseYearParam(searchParams.get('year'));
  const initialPeriodStart = useMemo(
    () => (urlYear !== undefined ? leaveYearStart(urlYear, leaveYearStartMonth) : undefined),
    [urlYear, leaveYearStartMonth]
  );

  const onEdit = (key: string, edit: LeaveBalanceEdit) => setEdits(prev => ({ ...prev, [key]: edit }));
  const onClearEdit = (key: string) =>
    setEdits(prev => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  const pending = Object.values(edits);
  const hasInvalid = pending.some(edit => !isValidAmount(edit.amount));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (hasInvalid) return;
    if (pending.length === 0) {
      goBack();
      return;
    }

    setSaving(true);
    try {
      // Sequential and oldest-period-first. Recurring overrides depend on order
      // ("apply to all future periods" / "stop after this period"), so parallel
      // requests could race on the backend.
      const ordered = [...pending].sort((a, b) => a.periodStart.localeCompare(b.periodStart));

      let lastResult;
      for (const edit of ordered) {
        lastResult = await setAllowance({
          allowance: {
            employeeId: employee.identityId,
            leaveTypeId: edit.leaveTypeId,
            periodStart: edit.periodStart,
            amount: Number(edit.amount),
            recurring: edit.isRecurring
          }
        });
        if (lastResult.error) break;
      }

      if (lastResult) {
        await handleMutationResult(lastResult, {
          successMessage: s.saveSuccess,
          errorMessage: s.saveError,
          onSuccess: goBack
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <TSTitleCard title={s.cardTitle} icon={FiCalendar} className={TSTitleCardStyle}>
        <SubmissionErrorBoundary>
          <div className="flex flex-col gap-8">
            <p className="text-sm text-gray-700">{s.description}</p>

            <Suspense fallback={<TableLoader />}>
              <LeaveBalanceSection
                employeeId={employee.identityId}
                pickerLabel={s.selectYear}
                period={YEARLY}
                leaveYearStartMonth={leaveYearStartMonth}
                initialPeriodStart={initialPeriodStart}
                edits={edits}
                onEdit={onEdit}
                onClearEdit={onClearEdit}
              />
            </Suspense>

            {/* <Suspense fallback={<TableLoader />}>
              <LeaveBalanceSection
                employeeId={employee.identityId}
                title={s.monthly}
                pickerLabel={s.selectMonth}
                period={MONTHLY}
                edits={edits}
                onEdit={onEdit}
                onClearEdit={onClearEdit}
              />
            </Suspense> */}

            <div className="flex items-center gap-3">
              <Button onClick={goBack}>{strings.common.cancel}</Button>
              <Button primary type="submit" loading={saving} disabled={hasInvalid}>
                {strings.common.save}
              </Button>
            </div>
          </div>
        </SubmissionErrorBoundary>
      </TSTitleCard>
    </form>
  );
};
